import { useAnalytics } from "hooks/useAnalytics/";
import { getPublicationUrl } from "pages/media/utils";
import { AnalyticsEvents, ProjectSharingLinkOpenEvent } from "types/analytics";
import { useInstanceAccessToken } from "./useInstanceAccessToken";

export const useOpenPublication = ({
  projectId,
  publicationId,
  requiresAccessToken = false,
  instanceSlug,
  organizationSlug,
  isDocHeyGenius = false,
  isAudioOnly = false,
  mediaStatus,
  isLive = false,
  trackingPage = "Project",
}: {
  projectId: string;
  publicationId: string;
  requiresAccessToken?: boolean;
  instanceSlug?: string;
  organizationSlug?: string;
  isDocHeyGenius?: boolean;
  isAudioOnly?: boolean;
  mediaStatus: string;
  // Whether the instance's publication is currently live — sourced from the
  // publication status endpoint (MED-6885), not `mediaStatus` (media.status
  // never signals "published" anymore).
  isLive?: boolean;
  trackingPage?: ProjectSharingLinkOpenEvent["page"];
}) => {
  const { getAccessToken } = useInstanceAccessToken({
    projectId,
    instanceId: publicationId,
  });

  const publishedPublicationUrl = getPublicationUrl({
    projectId,
    publicationId,
    isDocHeyGenius,
    isAudioOnly,
    instanceSlug,
    organizationSlug,
  });
  const { trackEvent } = useAnalytics();

  const openPublication = async () => {
    trackEvent(AnalyticsEvents.ProjectExperienceOpen, {
      project_id: projectId,
      instance_id: publicationId,
      media_status: mediaStatus,
      published: isLive,
      page: trackingPage,
    });
    if (requiresAccessToken) {
      try {
        const accessToken = await getAccessToken();

        const url = getPublicationUrl({
          projectId,
          publicationId,
          accessToken,
          isDocHeyGenius,
          isAudioOnly,
          instanceSlug,
          organizationSlug,
        });
        window.open(url);
      } catch (error) {
        console.error("Failed to get access token:", error);
      }
    } else {
      window.open(publishedPublicationUrl);
    }
  };

  return { openPublication, publishedPublicationUrl };
};
